/** The slice of XMLHttpRequest the upload uses; lets tests inject a fake. */
export interface XhrLike {
  upload: { onprogress: ((event: { lengthComputable: boolean; loaded: number; total: number }) => void) | null };
  status: number;
  open(method: string, url: string): void;
  setRequestHeader(name: string, value: string): void;
  send(body: Blob): void;
  abort(): void;
  onload: (() => void) | null;
  onerror: (() => void) | null;
  onabort: (() => void) | null;
  ontimeout: (() => void) | null;
}

class UploadError extends Error {
  constructor(
    message: string,
    public readonly kind: 'http' | 'network' | 'aborted',
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

export interface UploadOptions {
  url: string;
  blob: Blob;
  contentType: string;
  onProgress: (percent: number) => void;
  createXhr?: () => XhrLike;
}

/** PUTs a file to a server-issued signed URL. XHR rather than fetch because fetch has no upload progress. */
export function uploadWithProgress(options: UploadOptions): Promise<void> {
  const xhr = (options.createXhr ?? (() => new XMLHttpRequest() as unknown as XhrLike))();
  return new Promise<void>((resolve, reject) => {
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        options.onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new UploadError(`Upload failed with status ${xhr.status}`, 'http', xhr.status));
    };
    xhr.onerror = () => reject(new UploadError('Upload failed: network error', 'network'));
    xhr.ontimeout = () => reject(new UploadError('Upload failed: timed out', 'network'));
    xhr.onabort = () => reject(new UploadError('Upload was cancelled', 'aborted'));
    xhr.open('PUT', options.url);
    xhr.setRequestHeader('Content-Type', options.contentType);
    xhr.send(options.blob);
  });
}
